import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface NovaFaturaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NovaFaturaModal({ open, onOpenChange }: NovaFaturaModalProps) {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState<Date>();
  const [description, setDescription] = useState('');

  // Fetch customers for selection
  const { data: customers = [], isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers-select'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('customers')
        .select('id, name, tax_id')
        .eq('company_id', profile.company_id)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: open
  });

  const createInvoice = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      // Convert amount from BRL string to cents
      const amountCents = Math.round(parseFloat(amount.replace(/\./g, '').replace(',', '.')) * 100);

      const { error } = await supabase
        .from('invoices')
        .insert({
          customer_id: customerId,
          company_id: profile.company_id,
          amount: amountCents,
          due_date: format(dueDate!, 'yyyy-MM-dd'),
          description: description || null,
          status: 'pending'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Fatura criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['all-invoices'] });
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast.error('Erro ao criar fatura: ' + error.message);
    }
  });

  const resetForm = () => {
    setCustomerId('');
    setAmount('');
    setDueDate(undefined);
    setDescription('');
  };

  const formatCurrencyInput = (value: string) => {
    // Remove non-numeric characters
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    
    // Convert to decimal
    const decimal = parseInt(numbers) / 100;
    
    // Format as BRL
    return decimal.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(formatCurrencyInput(e.target.value));
  };

  const isValid = customerId && amount && dueDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova Fatura</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer Select with Search */}
          <div className="space-y-2">
            <Label htmlFor="customer">Cliente *</Label>
            <Combobox
              options={customers.map((c) => ({
                value: c.id,
                label: c.name,
                sublabel: c.tax_id
              }))}
              value={customerId}
              onValueChange={setCustomerId}
              placeholder={loadingCustomers ? "Carregando..." : "Selecione ou pesquise um cliente"}
              searchPlaceholder="Pesquisar por nome..."
              emptyMessage="Nenhum cliente encontrado."
              disabled={loadingCustomers}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Valor *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
              <Input
                id="amount"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0,00"
                className="pl-10"
              />
            </div>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Vencimento *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione a data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  initialFocus
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional da fatura"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={() => createInvoice.mutate()} 
            disabled={!isValid || createInvoice.isPending}
          >
            {createInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Criar Fatura
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
